import React from 'react';
import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  MailOutlined,
  MessageOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { DashboardClarificationRequest } from '../../job-application/models/clarification.model';
import { formatDashboardDate } from '../utils/dashboard.utils';

interface Props {
  requests: DashboardClarificationRequest[];
  onOpenApplication: (applicationId: string, requestId: string) => void;
  onViewAll: () => void;
}

const RecentClarificationsPanel: React.FC<Props> = ({
  requests,
  onOpenApplication,
  onViewAll,
}) => {
  const awaiting = requests.filter(
    (request) => request.status === 'SENT'
  ).length;
  const answered = requests.filter(
    (request) => request.status === 'ANSWERED'
  ).length;

  return (
    <section
      className="dashboard-panel dashboard-clarifications"
      aria-labelledby="recent-clarifications-title"
    >
      <header className="dashboard-panel__header dashboard-clarifications__header">
        <div>
          <h2 id="recent-clarifications-title">Candidate clarifications</h2>
          <span>Recent secure requests sent to candidates</span>
        </div>
        <button className="dashboard-link" type="button" onClick={onViewAll}>
          View applications <ArrowRightOutlined aria-hidden />
        </button>
      </header>

      <div className="dashboard-clarifications__summary">
        <span>
          <ClockCircleOutlined /> <strong>{awaiting}</strong> awaiting candidate
        </span>
        <span>
          <CheckCircleOutlined /> <strong>{answered}</strong> ready to review
        </span>
      </div>

      {requests.length === 0 ? (
        <div className="dashboard-empty dashboard-clarifications__empty">
          <MessageOutlined aria-hidden />
          <strong>No active clarification requests</strong>
          <span>Sent requests and candidate answers will appear here.</span>
        </div>
      ) : (
        <div className="dashboard-clarifications__list">
          {requests.map((request) => (
            <button
              type="button"
              className="dashboard-clarification"
              key={request.id}
              onClick={() =>
                onOpenApplication(request.applicationId, request.id)
              }
            >
              <span
                className={`dashboard-clarification__status dashboard-clarification__status--${request.status.toLowerCase()}`}
              >
                {request.status === 'ANSWERED' ? (
                  <CheckCircleOutlined />
                ) : (
                  <ClockCircleOutlined />
                )}
              </span>
              <span className="dashboard-clarification__copy">
                <strong>{request.candidateName}</strong>
                <small>{request.jobTitle}</small>
              </span>
              <span className="dashboard-clarification__meta">
                <strong>
                  {request.questionCount} question
                  {request.questionCount === 1 ? '' : 's'}
                </strong>
                <small>
                  {request.status === 'ANSWERED'
                    ? `Answered ${formatDashboardDate(request.answeredAt ?? '')}`
                    : `Sent ${formatDashboardDate(request.sentAt ?? '')}`}
                </small>
              </span>
              <span
                className="dashboard-clarification__channels"
                aria-label="Delivery channels"
              >
                {request.emailDelivered ? <MailOutlined title="Email" /> : null}
                {request.whatsappDelivered ? (
                  <WhatsAppOutlined title="WhatsApp" />
                ) : null}
              </span>
              <span className="dashboard-clarification__label">
                {request.status === 'ANSWERED'
                  ? 'Review answers'
                  : 'Awaiting response'}
              </span>
            </button>
          ))}
        </div>
      )}
    </section>
  );
};

export default RecentClarificationsPanel;
